import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { TimepickerBody } from './TimepickerBody';
import { Body } from './TimepickerBody/Body';
import { Footer } from './TimepickerBody/Footer';
import { Header } from './TimepickerBody/Header';
import { ScrollablePanel } from './TimepickerBody/ScrollablePanel';
import TimepickerInput from './TimepickerInput';
import { TIMEPICKER_SCROLLABLE_VARIANTS } from './utils/constant';
import { formatTime } from './utils/helper';
import useIsMobile from '../Utils/customHooks/useIsMobile';
import styles from '@dtsl/css-timepicker/dist/index.module.css';
import { FloatingPanel } from '@dtsl/react/dist/components/FloatingPanel';
function Timepicker(props) {
  const {
    size,
    headerLabel,
    time,
    onChange,
    onTriggerClick,
    onNow = () => {},
    nowLabel = 'Now',
    okLabel = 'OK',
    onClose = () => {},
    onConfirm = () => {},
    minuteInterval = 1,
    is24HourFormat = false,
    className = '',
    floatingPanelClassName = ''
  } = props;
  const {
    isMobile
  } = useIsMobile(640);
  const inputRef = useRef(null);
  const floatingRef = useRef(null);
  const triggerRef = useRef(null);
  const [isFloatingPanelVisible, setIsFloatingPanelVisible] = useState(false);

  // To Get the width of the panel
  let panelWidth = 0;
  if (inputRef.current && triggerRef.current) {
    const triggerCenter = triggerRef.current.getBoundingClientRect()?.left + triggerRef.current.getBoundingClientRect()?.width / 2;
    const inputElementWidth = inputRef.current.getBoundingClientRect()?.left;
    panelWidth = triggerCenter - inputElementWidth;
  }
  const floatingPanelInlineStyle = {
    width: panelWidth,
    maxWidth: isMobile ? '' : 'calc(var(--brand-spacing_64) + var(--brand-spacing_8))'
  };
  let formattedTimeForPanel = formatTime(time, is24HourFormat);
  /* eslint-disable data-test/onChange */
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TimepickerInput, {
    className: className,
    size: size,
    is24HourFormat: is24HourFormat,
    triggerRef: triggerRef,
    onChange: onChange,
    time: time,
    setIsFloatingPanelVisible: setIsFloatingPanelVisible,
    isFloatingPanelVisible: isFloatingPanelVisible,
    onTriggerClick: onTriggerClick,
    ref: inputRef
  }), /*#__PURE__*/React.createElement(FloatingPanel, {
    isVisible: isFloatingPanelVisible,
    setIsVisible: setIsFloatingPanelVisible,
    style: floatingPanelInlineStyle,
    ref: floatingRef,
    triggerRef: triggerRef,
    className: floatingPanelClassName
  }, /*#__PURE__*/React.createElement(TimepickerBody, null, /*#__PURE__*/React.createElement(Header, {
    headerLabel: headerLabel,
    setIsFloatingPanelVisible: setIsFloatingPanelVisible,
    onClose: onClose,
    tabIndex: 0
  }), /*#__PURE__*/React.createElement(Body, {
    tabIndex: 0
  }, /*#__PURE__*/React.createElement(ScrollablePanel, {
    range: is24HourFormat ? 24 : 12,
    key: `hour-panel`,
    selectedValue: formattedTimeForPanel.hours,
    type: TIMEPICKER_SCROLLABLE_VARIANTS.HOURS,
    dataTestId: `hour-panel`,
    onChange: onChange,
    time: time,
    tabIndex: 0
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['body-Vertical-div']
  }), /*#__PURE__*/React.createElement(ScrollablePanel, {
    range: 60,
    key: `minute-panel`,
    interval: minuteInterval,
    selectedValue: formattedTimeForPanel.minutes,
    dataTestId: `minute-panel`,
    onChange: onChange,
    time: time,
    type: TIMEPICKER_SCROLLABLE_VARIANTS.MINUTES,
    tabIndex: 0
  }), !is24HourFormat ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles['body-Vertical-div']
  }), /*#__PURE__*/React.createElement(ScrollablePanel, {
    range: ['AM', 'PM'],
    key: `ampm-panel`,
    dataTestId: `ampm-panel`,
    selectedValue: formattedTimeForPanel.ampm,
    onChange: onChange,
    time: time,
    type: TIMEPICKER_SCROLLABLE_VARIANTS.AM_PM,
    tabIndex: 0
  })) : null), /*#__PURE__*/React.createElement(Footer, {
    onConfirm: onConfirm,
    onNow: onNow,
    nowLabel: nowLabel,
    okLabel: okLabel,
    minuteInterval: minuteInterval,
    onChange: onChange,
    setIsFloatingPanelVisible: setIsFloatingPanelVisible,
    tabIndex: 0
  }))));
}
Timepicker.propTypes = {
  size: PropTypes.string,
  onConfirm: PropTypes.func,
  onNow: PropTypes.func,
  nowLabel: PropTypes.string.isRequired,
  okLabel: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  headerLabel: PropTypes.string.isRequired,
  time: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  minuteInterval: PropTypes.number,
  is24HourFormat: PropTypes.bool,
  className: PropTypes.string,
  floatingPanelClassName: PropTypes.string,
  onTriggerClick: PropTypes.func
};
export default /*#__PURE__*/React.memo(Timepicker);